// AuditLog — operator activity history.
//
// Reads app.audit_log via /admin/audit-log with filters by action prefix,
// entity type, free-text search, and date range. Row click opens a side
// panel with the before/after JSON diff for inspection.

import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useQuery,
} from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import {
  PageHeader,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import {
  fetchAdminAuditLog,
  fetchAdminAuditLogDetail,
  fetchAdminAuditLogFacets,
  type AdminAuditLogParams,
  type AdminAuditLogRow,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) {
    return `Today ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  return d.toLocaleString();
};

const COLUMNS = (
  onSelect: (row: AdminAuditLogRow) => void,
  selectedId: string | null,
): DataTableColumn<AdminAuditLogRow>[] => [
  {
    key: 'createdAt',
    h: 'When',
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {fmtTime(r.createdAt)}
      </span>
    ),
  },
  {
    key: 'actor',
    h: 'Actor',
    cell: (r) => (
      <span className="truncate">{r.actorEmail ?? 'system'}</span>
    ),
  },
  {
    key: 'action',
    h: 'Action',
    cell: (r) => (
      <span className="font-mono text-[11.5px]">{r.action}</span>
    ),
  },
  {
    key: 'entity',
    h: 'Entity',
    cell: (r) => (
      <span className="text-[12px]">
        <span className="font-medium">{r.entityType}</span>
        <span className="ms-1 font-mono text-[11px] text-[color:var(--bo-mute)]">
          #{r.entityId}
        </span>
      </span>
    ),
  },
  {
    key: 'ip',
    h: 'IP',
    cell: (r) => (
      <span className="font-mono text-[11px] text-[color:var(--bo-mute)]">
        {r.ip ?? '—'}
      </span>
    ),
  },
  {
    key: 'inspect',
    h: '',
    right: true,
    cell: (r) => (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(r);
        }}
        className={`rounded-md border px-2 py-1 text-[11px] font-medium transition ${
          selectedId === r.id
            ? 'border-[color:var(--bo-accent)] bg-[color:var(--bo-accent)]/10 text-[color:var(--bo-ink)]'
            : 'border-[color:var(--bo-line)] hover:border-[color:var(--bo-mute)]'
        }`}
      >
        Inspect
      </button>
    ),
  },
];

export function AuditLog() {
  const [q, setQ] = useState('');
  const [actionPrefix, setActionPrefix] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<string | null>(null);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { page, setPage, limit, setLimit } = usePager();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [q, actionPrefix, entityType, since, until, setPage]);

  const params: AdminAuditLogParams = {
    q: q.trim() || undefined,
    actionPrefix: actionPrefix ?? undefined,
    entityType: entityType ?? undefined,
    since: since ? new Date(since).toISOString() : undefined,
    until: until ? new Date(until).toISOString() : undefined,
    page,
    limit,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'audit-log', params],
    queryFn: ({ signal }) => fetchAdminAuditLog(params, signal),
    placeholderData: keepPreviousData,
  });

  const facetsQ = useQuery({
    queryKey: ['admin', 'audit-log', 'facets'],
    queryFn: ({ signal }) => fetchAdminAuditLogFacets(signal),
  });

  const detailQ = useQuery({
    queryKey: ['admin', 'audit-log', 'detail', selectedId],
    queryFn: ({ signal }) =>
      selectedId ? fetchAdminAuditLogDetail(selectedId, signal) : null,
    enabled: !!selectedId,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const actionPrefixes = facetsQ.data?.actionPrefixes ?? [];
  const entityTypes = facetsQ.data?.entityTypes ?? [];

  const hasFilters =
    !!q.trim() || !!actionPrefix || !!entityType || !!since || !!until;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="System"
        title="Audit log"
        sub={`${total.toLocaleString()} matching entries (append-only).`}
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load audit log: {(error as Error).message}
        </div>
      ) : null}

      <Toolbar>
        <ToolSearch
          value={q}
          onChange={setQ}
          placeholder="Search action, actor, entity…"
        />
        <input
          type="date"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          aria-label="Since"
          className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
        <input
          type="date"
          value={until}
          onChange={(e) => setUntil(e.target.value)}
          aria-label="Until"
          className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
        {hasFilters ? (
          <button
            type="button"
            onClick={() => {
              setQ('');
              setActionPrefix(null);
              setEntityType(null);
              setSince('');
              setUntil('');
            }}
            className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)]"
          >
            Reset
          </button>
        ) : null}
      </Toolbar>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          Action
        </span>
        <ToolChip
          active={actionPrefix === null}
          onClick={() => setActionPrefix(null)}
        >
          All
        </ToolChip>
        {actionPrefixes.slice(0, 12).map((p) => (
          <ToolChip
            key={p.prefix}
            active={actionPrefix === p.prefix}
            onClick={() => setActionPrefix(p.prefix)}
          >
            {p.prefix}
            <span className="ms-1 tabular-nums text-[10.5px] text-[color:var(--bo-mute)]">
              {p.count}
            </span>
          </ToolChip>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          Entity
        </span>
        <ToolChip
          active={entityType === null}
          onClick={() => setEntityType(null)}
        >
          All
        </ToolChip>
        {entityTypes.slice(0, 12).map((e) => (
          <ToolChip
            key={e.entityType}
            active={entityType === e.entityType}
            onClick={() => setEntityType(e.entityType)}
          >
            {e.entityType}
            <span className="ms-1 tabular-nums text-[10.5px] text-[color:var(--bo-mute)]">
              {e.count}
            </span>
          </ToolChip>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_400px]">
        <DataTable
          columns={COLUMNS((row) => setSelectedId(row.id), selectedId)}
          rows={rows}
          emptyLabel={isLoading ? 'Loading…' : 'No matching audit entries.'}
          pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
        />

        <aside className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[13px] font-semibold tracking-tight">
              Entry detail
            </div>
            {selectedId ? (
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                aria-label="Close"
                className="text-[11.5px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
              >
                Clear
              </button>
            ) : null}
          </div>
          {!selectedId ? (
            <div className="rounded-lg border border-dashed border-[color:var(--bo-line)] px-4 py-8 text-center text-[12px] text-[color:var(--bo-mute)]">
              Click <span className="font-medium">Inspect</span> on a row to view
              the before / after snapshot.
            </div>
          ) : detailQ.isLoading ? (
            <div className="text-[12px] text-[color:var(--bo-mute)]">
              Loading…
            </div>
          ) : detailQ.error ? (
            <div className="text-[12px] text-[#9f1239]">
              Couldn&apos;t load: {(detailQ.error as Error).message}
            </div>
          ) : detailQ.data ? (
            <div className="space-y-3 text-[12px]">
              <div>
                <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                  Action
                </div>
                <div className="font-mono text-[12px]">
                  {detailQ.data.action}
                </div>
              </div>
              <div>
                <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                  Entity
                </div>
                <div>
                  <span className="font-medium">
                    {detailQ.data.entityType}
                  </span>
                  <span className="ms-1 font-mono text-[11px] text-[color:var(--bo-mute)]">
                    #{detailQ.data.entityId}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                    Actor
                  </div>
                  <div>{detailQ.data.actorEmail ?? 'system'}</div>
                </div>
                <div>
                  <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                    When
                  </div>
                  <div className="tabular-nums">
                    {fmtTime(detailQ.data.createdAt)}
                  </div>
                </div>
              </div>
              {detailQ.data.ip ? (
                <div>
                  <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                    IP
                  </div>
                  <div className="font-mono">{detailQ.data.ip}</div>
                </div>
              ) : null}
              <div>
                <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                  Before
                </div>
                <pre className="overflow-x-auto rounded-md bg-[color:var(--bo-bg)] p-3 text-[11px]">
                  {detailQ.data.before
                    ? JSON.stringify(detailQ.data.before, null, 2)
                    : '—'}
                </pre>
              </div>
              <div>
                <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                  After
                </div>
                <pre className="overflow-x-auto rounded-md bg-[color:var(--bo-bg)] p-3 text-[11px]">
                  {detailQ.data.after
                    ? JSON.stringify(detailQ.data.after, null, 2)
                    : '—'}
                </pre>
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
